/**
 * AdminSettings.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import ThemeSelector from './ThemeSelector';
import './AdminSettings.css';

const AdminSettings = () => {
  const [showInventory, setShowInventory] = useState(true);
  const [saveMessage, setSaveMessage] = useState('');

  // Backup management state
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupMessage, setBackupMessage] = useState('');
  const [backupError, setBackupError] = useState('');

  // Backup configuration state
  const [backupConfig, setBackupConfig] = useState({
    retentionCount: 5,
    schedule: '0 2 * * *',
    backupDir: './backups'
  });
  const [configEditing, setConfigEditing] = useState(false);
  const [configMessage, setConfigMessage] = useState('');
  const [configError, setConfigError] = useState('');

  // AI Configuration state
  const [aiConfig, setAiConfig] = useState({
    watsonx_api_key: '',
    watsonx_project_id: '',
    watsonx_url: '',
    openai_api_key: '',
    newsletter_prompt: '',
    ai_provider: 'watsonx'
  });
  const [aiConfigMessage, setAiConfigMessage] = useState('');
  const [aiConfigError, setAiConfigError] = useState('');
  const [aiConfigLoading, setAiConfigLoading] = useState(false);

  useEffect(() => {
    try {
      const storedSetting = localStorage.getItem('showInventory');
      if (storedSetting !== null) {
        setShowInventory(JSON.parse(storedSetting));
      }
    } catch (error) {
      console.error("Failed to read 'showInventory' from localStorage", error);
    }

    // Load backups list and configuration
    loadBackups();
    // Load backups list and configuration
    loadBackups();
    loadBackupConfig();
    loadAiConfig();
  }, []);

  // Load AI configuration
  const loadAiConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/admin/settings/ai`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAiConfig(data);
      } else {
        console.error('Failed to load AI configuration');
      }
    } catch (error) {
      console.error('Error loading AI configuration:', error);
    }
  };

  // Update AI configuration
  const handleUpdateAiConfig = async (e) => {
    if (e) e.preventDefault();
    setAiConfigLoading(true);
    setAiConfigMessage('');
    setAiConfigError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/admin/settings/ai`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(aiConfig)
      });

      if (response.ok) {
        setAiConfigMessage('AI settings saved successfully!');
      } else {
        const errorData = await response.json();
        setAiConfigError(errorData.error || 'Failed to save AI settings');
      }
    } catch (error) {
      setAiConfigError('Error saving AI settings: ' + error.message);
    } finally {
      setAiConfigLoading(false);
      setTimeout(() => {
        setAiConfigMessage('');
        setAiConfigError('');
      }, 5000);
    }
  };

  const handleAiConfigChange = (field, value) => {
    setAiConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Load available backups
  const loadBackups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/admin/backup/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      } else {
        console.error('Failed to load backups');
      }
    } catch (error) {
      console.error('Error loading backups:', error);
    }
  };

  // Create a new backup
  const handleCreateBackup = async () => {
    setBackupLoading(true);
    setBackupMessage('');
    setBackupError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/admin/backup/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBackupMessage(`Backup created successfully: ${data.backup.filename}`);
        loadBackups(); // Reload the list
      } else {
        const errorData = await response.json();
        setBackupError(errorData.message || 'Failed to create backup');
      }
    } catch (error) {
      setBackupError('Error creating backup: ' + error.message);
    } finally {
      setBackupLoading(false);
      setTimeout(() => {
        setBackupMessage('');
        setBackupError('');
      }, 5000);
    }
  };

  // Download a backup (fetch + blob: Bearer auth cannot be sent on a plain <a href>)
  const handleDownloadBackup = async (filename) => {
    setBackupError('');
    try {
      const token = localStorage.getItem('token');
      const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url = `${base}/api/admin/backup/download/${encodeURIComponent(filename)}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let msg = `Download failed (${response.status})`;
        try {
          const errBody = await response.clone().json();
          if (errBody.message) msg = errBody.message;
          else if (errBody.error) msg = errBody.error;
        } catch (_) {
          /* not JSON */
        }
        setBackupError(msg);
        setTimeout(() => setBackupError(''), 8000);
        return;
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      // Do not use target="_blank" — it opens the blob in a new tab and is often blocked as a popup.
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setBackupError('Error downloading backup: ' + error.message);
      setTimeout(() => setBackupError(''), 8000);
    }
  };

  // Load backup configuration
  const loadBackupConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/admin/backup/config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBackupConfig(data.config);
      } else {
        console.error('Failed to load backup configuration');
      }
    } catch (error) {
      console.error('Error loading backup configuration:', error);
    }
  };

  // Update backup configuration
  const handleUpdateBackupConfig = async () => {
    setConfigMessage('');
    setConfigError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/admin/backup/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          retentionCount: backupConfig.retentionCount,
          schedule: backupConfig.schedule
        })
      });

      if (response.ok) {
        const data = await response.json();
        setConfigMessage(data.message + (data.config.note ? ' Note: ' + data.config.note : ''));
        setConfigEditing(false);
        loadBackupConfig(); // Reload configuration
      } else {
        const errorData = await response.json();
        setConfigError(errorData.error || 'Failed to update configuration');
      }
    } catch (error) {
      setConfigError('Error updating configuration: ' + error.message);
    } finally {
      setTimeout(() => {
        setConfigMessage('');
        setConfigError('');
      }, 8000);
    }
  };

  // Handle config field changes
  const handleConfigChange = (field, value) => {
    setBackupConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Format date for display
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const handleToggle = () => {
    const newShowInventory = !showInventory;
    setShowInventory(newShowInventory);
    try {
      localStorage.setItem('showInventory', JSON.stringify(newShowInventory));
      setSaveMessage('Settings saved locally!');
    } catch (error) {
      setSaveMessage('Failed to save settings.');
      console.error("Failed to save 'showInventory' to localStorage", error);
    }
    setTimeout(() => setSaveMessage(''), 2000);
  };

  return (
    <div className="admin-settings-container">
      <h2>Admin Settings</h2>

      {/* Theme Settings */}
      <div className="settings-section">
        <ThemeSelector />
      </div>

      <hr className="settings-divider" />

      {/* Display Settings */}
      <div className="settings-section">
        <h3>Display Settings</h3>
        <div className="setting-item">
          <label htmlFor="show-inventory">Show Product Inventory</label>
          <button
            id="show-inventory"
            onClick={handleToggle}
            className={`toggle-button ${showInventory ? 'active' : ''}`}
          >
            {showInventory ? 'ON' : 'OFF'}
          </button>
        </div>
        {saveMessage && <p className="save-message">{saveMessage}</p>}
      </div>

      <hr className="settings-divider" />

      {/* AI Configuration */}
      <div className="settings-section">
        <h3>AI Configuration</h3>
        <p className="section-description">Configure specific AI provider settings for automated content generation.</p>

        {aiConfigMessage && <div className="backup-message success">✅ {aiConfigMessage}</div>}
        {aiConfigError && <div className="backup-message error">❌ {aiConfigError}</div>}

        <form className="ai-config-form" onSubmit={handleUpdateAiConfig}>
          <div className="form-group">
            <label htmlFor="ai_provider">Default AI Provider</label>
            <select
              id="ai_provider"
              value={aiConfig.ai_provider}
              onChange={(e) => handleAiConfigChange('ai_provider', e.target.value)}
              className="config-input"
            >
              <option value="watsonx">IBM WatsonX</option>
              <option value="openai">OpenAI (ChatGPT)</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="newsletter_prompt">Newsletter Generation Prompt</label>
            <textarea
              id="newsletter_prompt"
              rows="4"
              value={aiConfig.newsletter_prompt || ''}
              onChange={(e) => handleAiConfigChange('newsletter_prompt', e.target.value)}
              className="config-input"
              placeholder="Enter instructions for the AI on how to generate newsletter content (e.g., 'Write a friendly and engaging newsletter highlighting the new products...')"
            />
            <small className="field-hint">This prompt will be used as the detailed instructions for generating the newsletter content.</small>
          </div>

          <div className="provider-section">
            <h4>IBM WatsonX Settings</h4>
            <div className="form-group">
              <label htmlFor="watsonx_api_key">API Key</label>
              <input
                type="password"
                id="watsonx_api_key"
                value={aiConfig.watsonx_api_key || ''}
                onChange={(e) => handleAiConfigChange('watsonx_api_key', e.target.value)}
                className="config-input"
                placeholder="IBM Cloud API Key"
              />
            </div>
            <div className="form-group">
              <label htmlFor="watsonx_project_id">Project ID</label>
              <input
                type="text"
                id="watsonx_project_id"
                value={aiConfig.watsonx_project_id || ''}
                onChange={(e) => handleAiConfigChange('watsonx_project_id', e.target.value)}
                className="config-input"
                placeholder="WatsonX Project ID"
              />
            </div>
            <div className="form-group">
              <label htmlFor="watsonx_url">Service URL (Optional)</label>
              <input
                type="text"
                id="watsonx_url"
                value={aiConfig.watsonx_url || ''}
                onChange={(e) => handleAiConfigChange('watsonx_url', e.target.value)}
                className="config-input"
                placeholder="https://us-south.ml.cloud.ibm.com"
              />
            </div>
          </div>

          <div className="provider-section">
            <h4>OpenAI Settings</h4>
            <div className="form-group">
              <label htmlFor="openai_api_key">API Key</label>
              <input
                type="password"
                id="openai_api_key"
                value={aiConfig.openai_api_key || ''}
                onChange={(e) => handleAiConfigChange('openai_api_key', e.target.value)}
                className="config-input"
                placeholder="sk-..."
              />
            </div>
          </div>

          <button
            type="submit"
            className="backup-button primary"
            disabled={aiConfigLoading}
          >
            {aiConfigLoading ? 'Saving...' : '💾 Save AI Configuration'}
          </button>
        </form>
      </div>

      <hr className="settings-divider" />

      {/* Database Backup Management */}
      <div className="settings-section">
        <h3>Database Backup Management</h3>

        <div className="backup-controls">
          <button
            onClick={handleCreateBackup}
            disabled={backupLoading}
            className="backup-button primary"
          >
            {backupLoading ? '⏳ Creating Backup...' : '💾 Create Backup Now'}
          </button>

          <button
            onClick={loadBackups}
            disabled={backupLoading}
            className="backup-button secondary"
          >
            🔄 Refresh List
          </button>
        </div>

        {backupMessage && (
          <div className="backup-message success">
            ✅ {backupMessage}
          </div>
        )}

        {backupError && (
          <div className="backup-message error">
            ❌ {backupError}
          </div>
        )}

        {/* Backup Configuration */}
        <div className="backup-config-section">
          <h4>⚙️ Backup Settings</h4>

          {configMessage && (
            <div className="backup-message success">
              ✅ {configMessage}
            </div>
          )}

          {configError && (
            <div className="backup-message error">
              ❌ {configError}
            </div>
          )}

          {!configEditing ? (
            // Display mode
            <div className="backup-info">
              <div className="config-display">
                <div className="config-item">
                  <strong>Number of Backups to Keep:</strong> {backupConfig.retentionCount}
                </div>
                <div className="config-item">
                  <strong>Backup Schedule:</strong> {backupConfig.schedule}
                  <span className="schedule-hint"> (Cron format: minute hour day month weekday)</span>
                </div>
                <div className="config-item">
                  <strong>Backup Directory:</strong> {backupConfig.backupDir}
                </div>
              </div>
              <button
                onClick={() => setConfigEditing(true)}
                className="backup-button secondary"
                style={{ marginTop: '10px' }}
              >
                ✏️ Edit Settings
              </button>
            </div>
          ) : (
            // Edit mode
            <div className="backup-config-form">
              <div className="form-group">
                <label htmlFor="retentionCount">
                  Number of Backups to Keep:
                  <span className="field-hint"> (1-100)</span>
                </label>
                <input
                  type="number"
                  id="retentionCount"
                  min="1"
                  max="100"
                  value={backupConfig.retentionCount}
                  onChange={(e) => handleConfigChange('retentionCount', parseInt(e.target.value))}
                  className="config-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="schedule">
                  Backup Schedule (Cron Format):
                  <span className="field-hint"> (minute hour day month weekday)</span>
                </label>
                <input
                  type="text"
                  id="schedule"
                  value={backupConfig.schedule}
                  onChange={(e) => handleConfigChange('schedule', e.target.value)}
                  placeholder="0 2 * * *"
                  className="config-input"
                />
                <div className="schedule-examples">
                  <small>
                    <strong>Examples:</strong><br />
                    • <code>0 2 * * *</code> - Daily at 2:00 AM<br />
                    • <code>0 */6 * * *</code> - Every 6 hours<br />
                    • <code>0 0 * * 0</code> - Weekly on Sunday at midnight<br />
                    • <code>0 3 * * 1</code> - Weekly on Monday at 3:00 AM
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label>
                  Backup Directory:
                </label>
                <input
                  type="text"
                  value={backupConfig.backupDir}
                  disabled
                  className="config-input disabled"
                  title="Backup directory cannot be changed at runtime"
                />
                <small className="field-note">
                  Note: Backup directory is set in the backend environment configuration and cannot be changed here.
                </small>
              </div>

              <div className="config-buttons">
                <button
                  onClick={handleUpdateBackupConfig}
                  className="backup-button primary"
                >
                  💾 Save Settings
                </button>
                <button
                  onClick={() => {
                    setConfigEditing(false);
                    loadBackupConfig(); // Reset to original values
                  }}
                  className="backup-button secondary"
                >
                  ❌ Cancel
                </button>
              </div>

              <div className="warning-note">
                ⚠️ <strong>Note:</strong> Changes to the backup schedule require a server restart to take effect.
              </div>
            </div>
          )}
        </div>

        <div className="backup-list">
          <h4>Available Backups ({backups.length})</h4>

          {backups.length === 0 ? (
            <p className="no-backups">No backups available. Create your first backup above.</p>
          ) : (
            <table className="backup-table">
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Created</th>
                  <th>Size</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup, index) => (
                  <tr key={backup.filename} className={index === 0 ? 'latest-backup' : ''}>
                    <td className="filename">{backup.filename}</td>
                    <td>{formatDate(backup.created)}</td>
                    <td>{backup.sizeFormatted}</td>
                    <td>
                      <button
                        onClick={() => handleDownloadBackup(backup.filename)}
                        className="action-button download"
                        title="Download backup"
                      >
                        ⬇️ Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings; 